import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import moment from "moment";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";

type PercentageOption = { label: string; value: 50 | 100 };

const PERCENTAGE_OPTIONS: PercentageOption[] = [
  { label: "1/2 or Less", value: 50 },
  { label: "Complete", value: 100 },
];

export type ExamDialogChargeOption = {
  id: number;
  name: string;
  amount: number;
};

export type ExamDialogPayload = {
  date: string;
  mode: "exam" | "re-exam";
  containerNo: string;
  containerSize: string;
  remarks: string;
  charges: Array<{
    paymentTypeId: number;
    paymentTypeName: string;
    percentage: 50 | 100;
    baseAmount: number;
    calculatedAmount: number;
  }>;
  totalAmount: number;
};

type Props = {
  open: boolean;
  mode: "exam" | "re-exam";
  containerNo: string;
  containerSize: string;
  chargeOptions: ExamDialogChargeOption[];
  onClose: () => void;
  onSave: (payload: ExamDialogPayload) => Promise<void> | void;
  saving?: boolean;
};

function normalizeSize(value: string): 20 | 40 | 45 | null {
  const s = String(value || "")
    .trim()
    .toLowerCase();
  if (!s) return null;
  if (s.includes("20")) return 20;
  if (s.includes("40")) return 40;
  if (s.includes("45")) return 45;
  const n = Number(s);
  if (Number.isFinite(n) && (n === 20 || n === 40 || n === 45)) return n as 20 | 40 | 45;
  return null;
}

export function ExamDialog({
  open,
  mode,
  containerNo,
  containerSize,
  chargeOptions,
  onClose,
  onSave,
  saving = false,
}: Props): React.ReactElement {
  const [date, setDate] = useState<string>(moment().format("YYYY-MM-DDTHH:mm"));
  const [stuffForkPct, setStuffForkPct] = useState<PercentageOption>(PERCENTAGE_OPTIONS[1]);
  const [destuffForkPct, setDestuffForkPct] = useState<PercentageOption>(PERCENTAGE_OPTIONS[1]);
  const [labourPct, setLabourPct] = useState<PercentageOption>(PERCENTAGE_OPTIONS[1]);
  const [remarks, setRemarks] = useState<string>("");

  const byName = useMemo(() => {
    const map = new Map<string, ExamDialogChargeOption>();
    for (const c of chargeOptions || []) {
      const key = String(c?.name || "")
        .trim()
        .toLowerCase();
      if (!key) continue;
      map.set(key, c);
    }
    return map;
  }, [chargeOptions]);

  const size = useMemo(() => normalizeSize(containerSize), [containerSize]);

  const pick = (name: string): ExamDialogChargeOption | null => {
    const key = String(name || "")
      .trim()
      .toLowerCase();
    return byName.get(key) || null;
  };

  const requiredChargeNames = useMemo(() => {
    const s = size;
    if (!s) return null;
    const pctSuffix = (pct: 50 | 100) => (pct === 100 ? "Complete" : "1/2 or Less");
    return {
      stuffFork: `Stuff ${s}ft ${pctSuffix(stuffForkPct.value)} Fork Lifter Charge`,
      destuffFork: `Destuff ${s}ft ${pctSuffix(destuffForkPct.value)} Fork Lifter Charge`,
      labour: `Stuff Destuff ${s}ft ${pctSuffix(labourPct.value)} Labour Charge`,
    };
  }, [size, stuffForkPct.value, destuffForkPct.value, labourPct.value]);

  const stuffForkCharge = useMemo(() => {
    const n = requiredChargeNames?.stuffFork;
    return n ? pick(n) : null;
  }, [requiredChargeNames]); // eslint-disable-line react-hooks/exhaustive-deps

  const destuffForkCharge = useMemo(() => {
    const n = requiredChargeNames?.destuffFork;
    return n ? pick(n) : null;
  }, [requiredChargeNames]); // eslint-disable-line react-hooks/exhaustive-deps

  const labourCharge = useMemo(() => {
    const n = requiredChargeNames?.labour;
    return n ? pick(n) : null;
  }, [requiredChargeNames]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    setDate(moment().format("YYYY-MM-DDTHH:mm"));
    setRemarks("");
    setStuffForkPct(PERCENTAGE_OPTIONS[1]);
    setDestuffForkPct(PERCENTAGE_OPTIONS[1]);
    setLabourPct(PERCENTAGE_OPTIONS[1]);
  }, [open]);

  const amountStuffFork = useMemo(
    () => Number(stuffForkCharge?.amount ?? 0) || 0,
    [stuffForkCharge]
  );
  const amountDestuffFork = useMemo(
    () => Number(destuffForkCharge?.amount ?? 0) || 0,
    [destuffForkCharge]
  );
  const amountLabour = useMemo(() => Number(labourCharge?.amount ?? 0) || 0, [labourCharge]);

  const total = useMemo(
    () => Math.round((amountStuffFork + amountDestuffFork + amountLabour) * 100) / 100,
    [amountStuffFork, amountDestuffFork, amountLabour]
  );

  const title = mode === "re-exam" ? "Re-Exam" : "Exam";
  const dateLabel = mode === "re-exam" ? "Re-Exam Date" : "Exam Date";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            Container No: <b>{containerNo || "-"}</b>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Container Size: <b>{containerSize || "-"}</b>
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <CommonTextFieldDate
              cssClassName="customTextField"
              label={dateLabel}
              name={"examDate"}
              value={date}
              onChange={(e: any) => setDate(moment(e).format("YYYY-MM-DDTHH:mm"))}
              maxDate={moment().format("YYYY-MM-DDTHH:mm")}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Charge"
              value={requiredChargeNames?.stuffFork ?? ""}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
              error={Boolean(size) && !stuffForkCharge}
              helperText={Boolean(size) && !stuffForkCharge ? "Not found in configuration" : " "}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <CommonAutoCompleteField
              cssClassName="customTextField"
              label="Percentage"
              placeholder="Select"
              name="stuffForkPct"
              disableClearable={true}
              optionsType={PERCENTAGE_OPTIONS}
              getOptionLabel={(o: any) => o?.label || ""}
              value={stuffForkPct}
              onChange={(o: any) => setStuffForkPct(o || PERCENTAGE_OPTIONS[1])}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Amount"
              value={amountStuffFork}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Charge"
              value={requiredChargeNames?.destuffFork ?? ""}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
              error={Boolean(size) && !destuffForkCharge}
              helperText={Boolean(size) && !destuffForkCharge ? "Not found in configuration" : " "}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <CommonAutoCompleteField
              cssClassName="customTextField"
              label="Percentage"
              placeholder="Select"
              name="destuffForkPct"
              disableClearable={true}
              optionsType={PERCENTAGE_OPTIONS}
              getOptionLabel={(o: any) => o?.label || ""}
              value={destuffForkPct}
              onChange={(o: any) => setDestuffForkPct(o || PERCENTAGE_OPTIONS[1])}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Amount"
              value={amountDestuffFork}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Charge"
              value={requiredChargeNames?.labour ?? ""}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
              error={Boolean(size) && !labourCharge}
              helperText={Boolean(size) && !labourCharge ? "Not found in configuration" : " "}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <CommonAutoCompleteField
              cssClassName="customTextField"
              label="Percentage"
              placeholder="Select"
              name="labourPct"
              disableClearable={true}
              optionsType={PERCENTAGE_OPTIONS}
              getOptionLabel={(o: any) => o?.label || ""}
              value={labourPct}
              onChange={(o: any) => setLabourPct(o || PERCENTAGE_OPTIONS[1])}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Amount"
              value={amountLabour}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Total Amount"
              value={total}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              multiline
              minRows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: 16 }}>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (!size) return;
            if (!stuffForkCharge || !destuffForkCharge || !labourCharge) return;
            onSave({
              date,
              mode,
              containerNo,
              containerSize,
              remarks,
              charges: [
                {
                  paymentTypeId: stuffForkCharge.id,
                  paymentTypeName: stuffForkCharge.name,
                  percentage: stuffForkPct.value,
                  baseAmount: stuffForkCharge.amount,
                  calculatedAmount: amountStuffFork,
                },
                {
                  paymentTypeId: destuffForkCharge.id,
                  paymentTypeName: destuffForkCharge.name,
                  percentage: destuffForkPct.value,
                  baseAmount: destuffForkCharge.amount,
                  calculatedAmount: amountDestuffFork,
                },
                {
                  paymentTypeId: labourCharge.id,
                  paymentTypeName: labourCharge.name,
                  percentage: labourPct.value,
                  baseAmount: labourCharge.amount,
                  calculatedAmount: amountLabour,
                },
              ],
              totalAmount: total,
            });
          }}
          color="primary"
          variant="contained"
          disabled={saving || !size || !stuffForkCharge || !destuffForkCharge || !labourCharge}
          style={{ backgroundColor: "#72CA00" }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
